import { useMemo } from 'react';
import { Modal, Stack, Select, Button, Group, Text } from '@mantine/core';
import { useState } from 'react';
import type { ListlistDto } from '@lib2/model/listlist/ListlistDto';
import { AppContentUtils } from '../AppContentUtils';

interface AddChildDialogProps {
  opened: boolean;
  onClose: () => void;
  onAdd: (childLlId: number) => void;
  availableChildren: ListlistDto[];
  existingChildIds: number[];
  childTypeLabel: string;
  isPending?: boolean;
}

export function AddChildDialog({
  opened,
  onClose,
  onAdd,
  availableChildren,
  existingChildIds,
  childTypeLabel,
  isPending,
}: AddChildDialogProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Filter out already-assigned children
  const options = useMemo(() => {
    const existingSet = new Set(existingChildIds);
    return availableChildren
      .filter((ll) => !existingSet.has(ll.llr.llId))
      .sort((a, b) => a.llr.title.localeCompare(b.llr.title))
      .map((ll) => ({
        value: String(ll.llr.llId),
        label: `${ll.llr.title} (${AppContentUtils.getDisplayId(ll.llr.llId)})`,
      }));
  }, [availableChildren, existingChildIds]);

  const handleSubmit = () => {
    if (selectedId) {
      onAdd(parseInt(selectedId, 10));
      setSelectedId(null);
    }
  };

  const handleClose = () => {
    setSelectedId(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={`Add ${childTypeLabel}`}
      size="md"
    >
      <Stack gap="md">
        {options.length === 0 ? (
          <Text size="sm" c="dimmed">
            No available {childTypeLabel.toLowerCase()}s to add. Create one first from the
            Content page.
          </Text>
        ) : (
          <Select
            label={`Select ${childTypeLabel}`}
            placeholder={`Choose a ${childTypeLabel.toLowerCase()}`}
            data={options}
            value={selectedId}
            onChange={setSelectedId}
            searchable
            data-autofocus
          />
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedId || options.length === 0}
            loading={isPending}
          >
            Add {childTypeLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
